/**
 * Customer × job type prices — types and pure helpers (gap 1, DEC-383). The price a customer
 * pays for one job type, dated, in that job type's unit ($/bbl, $/h, $/load). These types
 * mirror the /v1/customer-prices contracts (gap1-05) exactly.
 *
 * Prices are append-only: a change is a new row with a start date (today or later); a row
 * dated today or later can be corrected (a higher revision wins that day). There is no Remove
 * (D-07). $0.00 is a price, never "No price" (D-09).
 */

import { ROLE_PERMISSIONS } from '@lodex-pro/shared/auth/permissions';
import {
  formatCentsAsDollars,
  hasSubCentPrecision,
  parseDollarsToCents,
  type PriceChange,
} from '@lodex-pro/shared/money';
import {
  rateTag,
  rateUnitSuffix,
  type RateSource,
  type RateSourceKind,
  type RatedBillingMethod,
} from '@lodex-pro/shared/rate-source';

export interface CustomerSummary {
  readonly id: string;
  readonly name: string;
  readonly abbreviation: string | null;
}

export interface DatedPrice {
  readonly rateCents: number;
  readonly effectiveFrom: string;
  readonly revision: number;
}

export interface JobTypePriceRow {
  readonly familyId: string;
  readonly code: string;
  readonly label: string;
  readonly billingMethod: RatedBillingMethod;
  /** The price in force today and where it comes from; null = "No price". */
  readonly today: { readonly rateCents: number; readonly source: RateSource } | null;
  /** This customer's own row in force today, in this unit. */
  readonly special: DatedPrice | null;
  /** Future days, highest revision per day, ascending. */
  readonly scheduled: readonly DatedPrice[];
  /** D-04: a price set in a unit this job type no longer bills in. */
  readonly otherUnit: {
    readonly rateCents: number;
    readonly billingMethod: RatedBillingMethod;
    readonly effectiveFrom: string;
  } | null;
}

export interface CustomerJobTypePricesResponse {
  readonly customer: CustomerSummary;
  readonly today: string;
  readonly rows: readonly JobTypePriceRow[];
}

/** GET /v1/customer-prices/:customerId — the tab's customer context. */
export interface CustomerPricesCustomerResponse {
  readonly id: string;
  readonly name: string;
  readonly abbreviation: string | null;
  /** Company-local 'YYYY-MM-DD'. */
  readonly today: string;
  /** MANAGE_CUSTOMER_PRICES; UX only, the API enforces it. */
  readonly canEdit: boolean;
}

export interface JobTypePriceHistoryRow {
  readonly id: string;
  readonly billingMethod: RatedBillingMethod;
  readonly rateCents: number;
  readonly effectiveFrom: string;
  readonly revision: number;
  readonly correctedSameDay: boolean;
  readonly notes: string | null;
  readonly createdAt: string;
  readonly createdByName: string | null;
  readonly importId: string | null;
}

export interface JobTypePriceHistoryResponse {
  readonly familyId: string;
  readonly label: string;
  readonly rows: readonly JobTypePriceHistoryRow[];
  readonly truncated: boolean;
}

export interface SetJobTypePriceBody {
  readonly familyId: string;
  readonly rateCents: number;
  readonly effectiveFrom: string;
  /** The revision this write replaces on that day, or null for a new day. */
  readonly replacesRevision: number | null;
  readonly notes?: string | null;
}

/** MANAGE_CUSTOMER_PRICES holders, read from ROLE_PERMISSIONS so they can't drift. UX only. */
export const MANAGE_CUSTOMER_PRICES_ROLES: ReadonlySet<string> = new Set(
  Object.entries(ROLE_PERMISSIONS)
    .filter(([, p]) => (p as ReadonlySet<string>).has('MANAGE_CUSTOMER_PRICES'))
    .map(([r]) => r),
);

export type JobTypePriceInput =
  | { readonly ok: true; readonly cents: number }
  | { readonly ok: false; readonly reason: string };

/**
 * The price box → whole cents, or why it can't be saved. Gap 1's own parser: S4's
 * `readPriceInput` (zone grid) also takes "END"; a job-type price has no end (D-07).
 * The per-unit ceiling is the API's (its message names the unit).
 */
export function readJobTypePriceInput(raw: string): JobTypePriceInput {
  if (hasSubCentPrecision(raw)) return { ok: false, reason: 'Use dollars and cents, e.g. 1.25.' };
  const r = parseDollarsToCents(raw);
  if (!r.ok) {
    return {
      ok: false,
      reason: r.reason === 'blank' ? 'Enter a price.' : 'Enter a price like 150.00.',
    };
  }
  if (r.cents < 0) return { ok: false, reason: 'A price cannot be negative.' };
  return { ok: true, cents: r.cents };
}

const SOURCE_TEXT: Readonly<Record<RateSourceKind, string>> = {
  CUSTOMER_JOB_TYPE: 'this customer',
  CUSTOMER: 'customer rate',
  JOB_TYPE: 'standard',
};

/** Where today's price comes from, as the table says it (D-06). */
export function sourceLabel(kind: RateSourceKind): string {
  return SOURCE_TEXT[kind];
}

/** "$150.00/h" — integer cents and a rated method. */
export function priceText(cents: number, billingMethod: string): string {
  return `$${formatCentsAsDollars(cents)}${rateUnitSuffix(billingMethod)}`;
}

/** The "Price today" cell: "$150.00/h (this customer)", "$0.00/h (this customer)" or "No price". */
export function priceCellText(row: JobTypePriceRow): string {
  if (row.today === null) return 'No price';
  return `${priceText(row.today.rateCents, row.billingMethod)} (${sourceLabel(row.today.source.kind)})`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** '2027-01-05' → 'Jan 5, 2027', from the string's parts (a calendar day, no time zone). */
export function formatDay(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (m === null) return iso;
  const month = MONTHS[Number(m[2]) - 1];
  if (month === undefined) return iso;
  return `${month} ${String(Number(m[3]))}, ${m[1] ?? ''}`;
}

/**
 * A write on a day that already has a change for this job type (today's special price or a
 * scheduled one) is a correction of it: the revision to send as `replacesRevision`, and the
 * notice the dialog shows. null = a new day.
 */
export function replacesNotice(
  row: JobTypePriceRow,
  day: string,
): { readonly revision: number; readonly text: string } | null {
  const hit =
    row.special !== null && row.special.effectiveFrom === day
      ? row.special
      : row.scheduled.find((s) => s.effectiveFrom === day);
  if (hit === undefined) return null;
  return {
    revision: hit.revision,
    text: `This replaces the change already made for ${formatDay(day)}.`,
  };
}

/**
 * D-15 — the source tag under a billed line's rate on the BILLING TICKET PAGE ONLY (never an
 * invoice, its PDF or an export): "$150.00/h · customer job-type rate (from Jan 1)". null
 * when the line has no stored source (every line but the primary, and lines approved before
 * 0271). `today` drops the year for a day in the current year; undefined keeps it.
 * A line without the field (an API from before gap1-04) reads as no source, not a crash.
 */
export function primaryLineTag(
  line: { readonly rateCents: number | null; readonly rateSource?: RateSource | null },
  billingMethod: string,
  today: string | undefined,
): string | null {
  const source = line.rateSource ?? null;
  if (source === null) return null;
  return rateTag(line.rateCents, billingMethod, source, today);
}

// ── gap 1 PR 2: many prices at once (Copy D-11, Raise prices D-13, Import D-12) ─────────────
// These mirror services/customer-job-type-rates/bulk.ts and routes/customer-prices/job-types-bulk.ts.

/** The `request` of POST /v1/customer-prices/job-types/bulk/preview and /apply. */
export type JobTypeBulkRequest =
  | {
      readonly kind: 'COPY';
      readonly fromCustomerId: string;
      readonly toCustomerId: string;
      /** The ticked job types (D-11); absent = every one offered. */
      readonly familyIds?: readonly string[];
      readonly effectiveFrom: string;
    }
  | {
      readonly kind: 'RAISE';
      readonly familyId: string;
      readonly customerIds: readonly string[];
      readonly change: PriceChange;
      /** "Also give customers without a special price one" (D-13). */
      readonly includeUnpriced: boolean;
      readonly effectiveFrom: string;
    }
  | {
      readonly kind: 'IMPORT';
      /** The sheet's cells, header row first, verbatim. */
      readonly rows: readonly (readonly string[])[];
      readonly effectiveFrom: string;
    };

export type JobTypeRowStatus = 'ADD' | 'CHANGE' | 'UNCHANGED' | 'ERROR';

export interface JobTypePreviewRow {
  /** The file line for IMPORT (header = line 1), else null. */
  readonly line: number | null;
  readonly customer: {
    readonly id: string;
    readonly name: string;
    readonly abbreviation: string | null;
  } | null;
  readonly jobType: {
    readonly familyId: string;
    readonly code: string;
    readonly label: string;
    readonly billingMethod: RatedBillingMethod;
  } | null;
  /** The price in force on effectiveFrom (special, else customer rate, else standard). */
  readonly currentCents: number | null;
  readonly currentSource: RateSourceKind | null;
  readonly newCents: number | null;
  readonly effectiveFrom: string;
  readonly status: JobTypeRowStatus;
  /** The highest revision already on that day (D-08); the write is it + 1. */
  readonly replacesRevision: number | null;
  readonly messages: readonly string[];
  /** IMPORT only: the row's Notes. */
  readonly notes: string | null;
}

export interface JobTypePreviewCounts {
  readonly add: number;
  readonly change: number;
  readonly unchanged: number;
  readonly error: number;
}

/** POST .../bulk/apply response. */
export interface JobTypeBulkApplyResponse {
  readonly applied: { readonly add: number; readonly change: number };
  readonly importId: string;
}

/** GET /v1/customer-prices/job-types: the live, rated job types and the company's today. */
export interface JobTypeCatalogResponse {
  /** Company-local 'YYYY-MM-DD'. */
  readonly today: string;
  readonly jobTypes: readonly {
    readonly familyId: string;
    readonly code: string;
    readonly label: string;
    readonly billingMethod: RatedBillingMethod;
  }[];
}
